/**
 * Jul-2026 (User-Wunsch) — Verkäufer-Provision als wiederverwendbarer Block.
 *
 * Zeigt die berechneten Provisionszeilen (Fahrzeug, Zusatzertrag, optional
 * Zubehör/Dienstleistung) samt Bestätigungs-Haken (Filialleiter + Buchhaltung).
 * Erst wenn BEIDE bestätigt haben, ist die Provision „abrufbar".
 *
 * Wird in der Provisionsabrechnung (CommissionSettlementModal) oberhalb der
 * Vermittler-Auszahlung eingebunden — beide Provisions-Funktionen an einem Ort.
 */
import { useEffect, useState } from "react";
import { Percent, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatPriceExact as formatPrice } from "../../../lib/api";

export default function SellerCommissionBlock({ listingId }) {
    const [sc, setSc] = useState(null);
    const [scBusy, setScBusy] = useState("");

    useEffect(() => {
        let alive = true;
        api.get(`/listings/${listingId}/dispo/seller-commission`)
            .then((r) => { if (alive) setSc(r.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, [listingId]);

    const toggleConf = async (line, role, val) => {
        setScBusy(`${line}-${role}`);
        try {
            const { data } = await api.patch(`/listings/${listingId}/dispo/seller-commission`, { line, role, confirmed: val });
            setSc(data);
        } catch {
            toast.error("Bestätigung konnte nicht gespeichert werden");
        } finally {
            setScBusy("");
        }
    };

    if (!sc) return null;

    const rows = [
        { key: "vehicle", label: "Fahrzeug-Provision", line: sc.lines.vehicle, hint: sc.lines.vehicle.rule_name || (sc.lines.vehicle.configured ? "keine passende Regel" : "keine Regeln hinterlegt") },
        { key: "extra", label: "Provision auf Zusatzertrag", line: sc.lines.extra, hint: `${formatPrice(sc.lines.extra.base || 0)} × ${sc.lines.extra.percent || 0}%` },
        ...(sc.lines.accessory && (sc.lines.accessory.amount > 0 || sc.lines.accessory.configured) ? [{ key: "accessory", label: "Zubehör-Provision", line: sc.lines.accessory, hint: sc.lines.accessory.rule_name || (sc.lines.accessory.configured ? "keine aktive Regel" : "keine Regeln hinterlegt") }] : []),
        ...(sc.lines.service && (sc.lines.service.amount > 0 || sc.lines.service.configured) ? [{ key: "service", label: "Dienstleistungs-Provision", line: sc.lines.service, hint: sc.lines.service.rule_name || (sc.lines.service.configured ? "keine aktive Regel" : "keine Regeln hinterlegt") }] : []),
    ];

    return (
        <div className="space-y-2" data-testid="dispo-seller-commission">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#111827]">
                <Percent className="w-3.5 h-3.5" /> Verkäufer-Provision
                {sc?.seller?.name && <span className="ml-1 normal-case font-normal text-gray-500">· {sc.seller.name}</span>}
            </div>
            <p className="text-[11px] text-gray-500 -mt-1">
                {sc?.seller?.name
                    ? "Erst wenn Filialleiter UND Buchhaltung bestätigt haben, ist die Provision abrufbar."
                    : "Kein Verkäufer zugeordnet."}
            </p>
            {rows.map((row) => (
                <div key={row.key} className="bg-white border-2 border-orange-200 rounded-sm p-2.5" data-testid={`commission-row-${row.key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                        <div className="min-w-0">
                            <div className="text-xs font-bold text-[#111827]">{row.label}</div>
                            <div className="text-[10px] text-gray-500 truncate">{row.hint}</div>
                        </div>
                        <div className="text-base font-black font-mono-num text-[#111827] shrink-0" data-testid={`commission-amount-${row.key}`}>{formatPrice(row.line.amount || 0)}</div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {[
                            { role: "filialleiter", label: "Filialleiter" },
                            { role: "buchhaltung", label: "Buchhaltung" },
                        ].map((c) => (
                            <label key={c.role} className="flex items-center gap-1.5 cursor-pointer select-none text-[11px] font-bold text-gray-700" data-testid={`commission-conf-${row.key}-${c.role}-label`}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(row.line.confirmed?.[c.role])}
                                    disabled={scBusy === `${row.key}-${c.role}`}
                                    onChange={(e) => toggleConf(row.key, c.role, e.target.checked)}
                                    className="w-4 h-4 accent-orange-600"
                                    data-testid={`commission-conf-${row.key}-${c.role}`}
                                />
                                {c.label}
                            </label>
                        ))}
                        {row.line.fully_confirmed && (
                            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-black uppercase text-emerald-700" data-testid={`commission-confirmed-${row.key}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" /> bestätigt
                            </span>
                        )}
                    </div>
                </div>
            ))}
            <div className="flex justify-between text-xs border-t-2 border-orange-300 pt-1.5">
                <span className="font-bold text-[#111827]">Bestätigt / abrufbar</span>
                <span className="font-black font-mono-num text-[#111827]" data-testid="commission-confirmed-total">{formatPrice(sc.confirmed_total || 0)} <span className="text-gray-400 font-normal">/ {formatPrice(sc.total || 0)}</span></span>
            </div>
        </div>
    );
}
